// HCSC (BCBS IL/MT/NM/OK/TX) commission statement parser.
//
// HCSC writes one consolidated statement covering 5 state Blues plans:
// BlueCross BlueShield of Illinois, Montana, New Mexico, Oklahoma,
// Texas. Producer NPN sees ONE ledger. Statement sequence is in the
// header (S0001 = first; gaps in the sequence = missing revenue
// history and we surface that as a warning).
//
// COMPENSATION MODEL
//   Calc Method = "$/Member"   → flat PMPM (per-member-per-month)
//   Comm Rate   = $20.00       → ACA on-exchange standard for BCBS
//   Split %     = 100          → no upline split
//   Contracts   = head count   → 1 single, 2 couple, 3+ family
//   Comm Amt    = Contracts × Rate × 1 month (always this period)
//   YTD Comm    = Contracts × Rate × Pol Months age in months
//
// STATEMENT BUCKETS (summary page)
//   COMMISSIONS
//     Commission Group Sub total                  — employer-sponsored
//     Individual Health New Business              — Y1 ACA medical
//     Individual Dental New Business              — HISC dental
//     Individual Health Retention                 — Y2+ ACA medical
//     Individual Dental Retention                 — Y2+ dental
//     Commission Individual Sub total
//   COMPENSATION TOTALS
//     Group Total
//     Individual Total
//     Prior Balance Forward                       — carryover from prior
//     GRAND TOTALS                                — period + YTD
//
// CRITICAL DATES PER ROW
//   Orig Eff Dt   → policy effective_date
//   PR Eff Dt     → producer-of-record effective (assignment)
//   Cancel Dt     → termination_date (status flips to 'cancelled')
//   Pd From Dt    → membership month this $ pays for → last_commission_at
//   Pd To Date    → end of that membership month
//
// LOSS-DETECTION TIE-IN
//   Every imported row writes Pd From Dt into health_policies.last_commission_at.
//   If next statement doesn't contain the policy and 45+ days pass, the
//   HealthPortalView payment-stopped detector flags it as a loss event.
//
// ANOMALY CLASSES we surface to the agent
//   1. ZERO_COMMISSION   — Comm Rate = $0 but policy active. CSR plan
//                          or carrier coding error. Recoverable; flag.
//   2. MISSING_SEQUENCE  — Sxxxx jumps. A statement was never received.
//   3. CHARGEBACK        — negative Comm Amt OR negative YTD bucket.
//   4. CANCELLATION      — Cancel Dt populated; status → 'cancelled'.
//   5. FIRST_MONTH       — Orig Eff Dt within last 35 days; watch retention.

import { providerLinks } from './coverageTemplates';

export const HCSC_CARRIERS = [
  'BCBS of Illinois',
  'BCBS of Montana',
  'BCBS of New Mexico',
  'BCBS of Oklahoma',
  'BCBS of Texas',
];

export const HCSC_BUCKETS = [
  { id: 'group_subtotal',          label: 'Commission Group Sub total',          group: 'commissions' },
  { id: 'individual_health_new',   label: 'Individual Health New Business',     group: 'commissions' },
  { id: 'individual_dental_new',   label: 'Individual Dental New Business',     group: 'commissions' },
  { id: 'individual_health_ret',   label: 'Individual Health Retention',        group: 'commissions' },
  { id: 'individual_dental_ret',   label: 'Individual Dental Retention',        group: 'commissions' },
  { id: 'individual_subtotal',     label: 'Commission Individual Sub total',    group: 'commissions' },
  { id: 'group_total',             label: 'Group Total',                         group: 'compensation' },
  { id: 'individual_total',        label: 'Individual Total',                    group: 'compensation' },
  { id: 'prior_balance_forward',   label: 'Prior Balance Forward',               group: 'compensation' },
  { id: 'grand_total',             label: 'GRAND TOTALS',                        group: 'compensation' },
];

// The detail-page column schema. Order matters when parsing tab/csv
// pastes. Each column carries a type hint so the parser coerces.
export const HCSC_DETAIL_COLUMNS = [
  { id: 'policy_number',     label: 'Acct/Policy',     type: 'text'  },
  { id: 'insured_name',      label: 'Acct/Pol Name',   type: 'text'  },
  { id: 'orig_eff_dt',       label: 'Orig Eff Dt',     type: 'date'  },
  { id: 'group_no',          label: 'Group No',        type: 'text'  },
  { id: 'product_name',      label: 'Product Name',    type: 'text'  },
  { id: 'pr_eff_dt',         label: 'PR Eff Dt',       type: 'date'  },
  { id: 'cancel_dt',         label: 'Cancel Dt',       type: 'date'  },
  { id: 'calc_method',       label: 'Calc Method',     type: 'text'  },
  { id: 'funding_type',      label: 'Funding Type',    type: 'text'  },
  { id: 'contracts',         label: 'Contracts',       type: 'int'   },
  { id: 'split_pct',         label: 'Split %',         type: 'float' },
  { id: 'pol_mos',           label: 'Pol Mos',         type: 'int'   },
  { id: 'pd_from_dt',        label: 'Pd From Dt',      type: 'date'  },
  { id: 'pd_to_dt',          label: 'Pd To Date',      type: 'date'  },
  { id: 'prem_applied',      label: 'Prem Applied',    type: 'money' },
  { id: 'comm_rate',         label: 'Comm Rate',       type: 'money' },
  { id: 'comm_amt',          label: 'Comm Amt',        type: 'money' },
  { id: 'ytd_premium',       label: 'YTD Premium',     type: 'money' },
  { id: 'ytd_commission',    label: 'YTD Commission',  type: 'money' },
  { id: 'source_vendor',     label: 'Source / Vendor', type: 'text'  },
];

// Normalize an HCSC date string. Statements use MM/DD/YY.
export function parseHcscDate(s) {
  if (!s) return null;
  const m = String(s).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!m) return null;
  const mm = String(m[1]).padStart(2, '0');
  const dd = String(m[2]).padStart(2, '0');
  let yy = m[3];
  if (yy.length === 2) yy = (Number(yy) >= 70 ? '19' : '20') + yy;
  return `${yy}-${mm}-${dd}`;
}

// Strip currency formatting → number. Handles "$340.00", "-$12.00", "(12.00)".
export function parseHcscMoney(s) {
  if (s === null || s === undefined || s === '') return 0;
  let str = String(s).trim();
  let neg = false;
  if (str.startsWith('(') && str.endsWith(')')) { neg = true; str = str.slice(1, -1); }
  str = str.replace(/[\$,]/g, '');
  if (str.startsWith('-')) { neg = true; str = str.slice(1); }
  const n = parseFloat(str);
  if (Number.isNaN(n)) return 0;
  return neg ? -n : n;
}

// Insured name on the statement comes through as "LAST, FIRST [INITIAL]" with
// some truncation (e.g. "GAMBRELL, ALIZ" was truncated from ALIZABETH). We
// title-case it without losing the truncation hint.
export function normalizeInsuredName(raw) {
  if (!raw) return '';
  const parts = String(raw).split(',').map(s => s.trim());
  const title = (w) => w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : '';
  if (parts.length === 1) return parts[0].split(/\s+/).map(title).join(' ');
  const [last, firstish] = parts;
  return `${firstish.split(/\s+/).map(title).join(' ')} ${title(last)}`.trim();
}

// HCSC consolidated statement → which state-Blues subsidiary?
// Statement page 2 lists all 5; the actual policy carrier is inferred
// from the producer's licensed state OR the row's group prefix if present.
// Caller can override per-row when known.
export function inferHcscCarrier(producerState) {
  const s = String(producerState || '').toUpperCase();
  switch (s) {
    case 'IL': return 'BCBS of Illinois';
    case 'MT': return 'BCBS of Montana';
    case 'NM': return 'BCBS of New Mexico';
    case 'OK': return 'BCBS of Oklahoma';
    case 'TX': return 'BCBS of Texas';
    default:   return 'BCBS (HCSC)';
  }
}

// Parse a single row of CSV/TSV/whitespace-pasted HCSC detail.
// Returns null if the row clearly isn't a policy line (header,
// subtotal, etc.) — caller filters.
export function parseHcscDetailRow(line, opts = {}) {
  if (!line) return null;
  const text = String(line);
  // Skip header / totals / footers
  const ucase = text.toUpperCase();
  if (/^(\s*=+|TOTAL|GRAND|PRODUCER|SELLING|INDIVIDUAL|COMMISSIONS|COMPENSATION|GROUP|PAGE\s+\d|\*\s*INDICATES)/i.test(text)) return null;
  if (!/[A-Z],/.test(ucase)) return null; // needs an insured "LAST, FIRST"

  // Try tab first, then comma, then 2+ spaces.
  let cells;
  if (text.includes('\t'))      cells = text.split('\t');
  else if (text.includes(','))  cells = text.match(/("[^"]*"|[^,]+)/g) || [];
  else                          cells = text.split(/\s{2,}/);
  cells = cells.map(c => String(c).replace(/^"|"$/g, '').trim());

  if (cells.length < 6) return null; // not enough to be a row

  // Heuristic field-mapping: pick the first cell that looks like a policy
  // number (9-10 digits) as policy_number; the first cell containing a
  // comma as insured_name; first MM/DD/YY as eff date; etc.
  const policy = cells.find(c => /^\d{9,10}$/.test(c));
  const insuredCell = cells.find(c => /[A-Z]{2,}[A-Z\-]*,\s*[A-Z]/i.test(c));
  const dates = cells.filter(c => /^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(c));
  const moneys = cells.filter(c => /^\$?-?\d+(?:\.\d{2})?$/.test(c)).map(parseHcscMoney);
  const contracts = (() => {
    // Contracts is typically a small int 1-9 in the row. After insured name.
    const candidate = cells.find((c, i) => /^[1-9]$/.test(c) && i > 3);
    return candidate ? parseInt(candidate, 10) : 1;
  })();
  const polMos = (() => {
    const cands = cells.filter((c) => /^[1-9]$/.test(c));
    // Pol Mos is usually the second small-int cell after Contracts.
    return cands.length >= 2 ? parseInt(cands[1], 10) : null;
  })();
  // Source vendor is the trailing all-caps token (AGILITY, HEALTHSHERPA, etc.)
  const vendor = (() => {
    const last = cells[cells.length - 1];
    if (last && /^[A-Z]{4,}$/.test(last)) return last;
    return null;
  })();

  return {
    raw: text,
    policy_number: policy || null,
    insured_name: insuredCell ? normalizeInsuredName(insuredCell) : null,
    orig_eff_dt:  parseHcscDate(dates[0]),
    pr_eff_dt:    parseHcscDate(dates[1]),
    cancel_dt:    parseHcscDate(dates[2]),     // null when no cancel
    pd_from_dt:   parseHcscDate(dates[dates.length - 2]),
    pd_to_dt:     parseHcscDate(dates[dates.length - 1]),
    contracts,
    pol_mos: polMos,
    calc_method: '$/Member',
    split_pct: 100,
    comm_rate:     moneys.length >= 4 ? moneys[moneys.length - 4] : null,
    comm_amt:      moneys.length >= 3 ? moneys[moneys.length - 3] : null,
    ytd_premium:   moneys.length >= 2 ? moneys[moneys.length - 2] : null,
    ytd_commission:moneys.length >= 1 ? moneys[moneys.length - 1] : null,
    source_vendor: vendor,
    plan_name: cells.find(c => /BLUE|HEALTH|MYBLUE|BLUE\s*HEALTH/i.test(c)) || 'BCBS MyBlue',
    carrier: inferHcscCarrier(opts.producerState),
  };
}

// Walk a multi-line paste and produce structured rows + a parse summary.
export function parseHcscStatement(text, opts = {}) {
  const lines = String(text || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const rows  = lines.map(l => parseHcscDetailRow(l, opts)).filter(Boolean);
  return rows;
}

// Analyze parsed rows — the CPA layer. Surface anomalies + totals.
// Pass producer's last_known_policies (Set of policy_numbers from prior
// statement) to detect DROPPED policies (loss candidates).
export function analyzeHcscRows(rows, opts = {}) {
  const summary = {
    rowCount: rows.length,
    totalCommThisPeriod: 0,
    totalYtdCommission: 0,
    activeContracts: 0,
    bySource: {},
    byPlan: {},
    byCarrier: {},
  };
  const anomalies = [];

  for (const r of rows) {
    summary.totalCommThisPeriod += Number(r.comm_amt) || 0;
    summary.totalYtdCommission  += Number(r.ytd_commission) || 0;
    summary.activeContracts     += Number(r.contracts) || 0;
    if (r.source_vendor) summary.bySource[r.source_vendor] = (summary.bySource[r.source_vendor] || 0) + 1;
    if (r.plan_name)     summary.byPlan[r.plan_name]       = (summary.byPlan[r.plan_name] || 0) + 1;
    if (r.carrier)       summary.byCarrier[r.carrier]      = (summary.byCarrier[r.carrier] || 0) + 1;

    // ZERO_COMMISSION — paid policy with $0 rate. Surface for the agent
    // to investigate (CSR-eligible plan, carrier suspense, or coding err).
    if ((Number(r.comm_rate) === 0 || r.comm_rate === null) && !r.cancel_dt) {
      anomalies.push({
        kind: 'ZERO_COMMISSION',
        severity: 'amber',
        policy: r.policy_number,
        insured: r.insured_name,
        message: 'Active policy earning $0 commission. Likely CSR-eligible silver plan or carrier suspense — verify with broker support before next statement.',
      });
    }
    // CANCELLATION — cancel date present.
    if (r.cancel_dt) {
      anomalies.push({
        kind: 'CANCELLATION',
        severity: 'red',
        policy: r.policy_number,
        insured: r.insured_name,
        message: `Cancelled ${r.cancel_dt}. MRR off the book: $${(Number(r.comm_rate) || 0) * (Number(r.contracts) || 1)}/mo.`,
      });
    }
    // FIRST_MONTH — eff date within 35 days; flag for retention watch.
    if (r.orig_eff_dt) {
      const ageDays = Math.floor((Date.now() - new Date(r.orig_eff_dt + 'T00:00:00').getTime()) / 86400000);
      if (ageDays >= 0 && ageDays <= 35) {
        anomalies.push({
          kind: 'FIRST_MONTH',
          severity: 'slate',
          policy: r.policy_number,
          insured: r.insured_name,
          message: `New enrollment (${ageDays}d old). Watch month-2 retention — first-month attrition kills lead ROI.`,
        });
      }
    }
  }

  // DROPPED — policy was on the prior statement but not this one.
  if (opts.priorPolicies && opts.priorPolicies instanceof Set) {
    const present = new Set(rows.map(r => r.policy_number).filter(Boolean));
    opts.priorPolicies.forEach(pn => {
      if (!present.has(pn)) {
        anomalies.push({
          kind: 'DROPPED',
          severity: 'red',
          policy: pn,
          insured: null,
          message: 'Was on the prior statement; missing from this one. Commission stopped — treat as off-the-book.',
        });
      }
    });
  }

  return { summary, anomalies };
}

// Build a health_policies upsert payload for a parsed row. The caller
// supplies owner_id + (optional) client_id linkage.
export function rowToHealthPolicyUpsert(r, { ownerId, clientId, producerProdNum }) {
  if (!r || !r.policy_number) return null;
  const monthlyCommission = (Number(r.comm_rate) || 0) * (Number(r.contracts) || 1);
  return {
    owner_id: ownerId,
    client_id: clientId || null,
    client_name: r.insured_name,
    carrier: r.carrier || 'BCBS (HCSC)',
    plan_name: r.plan_name,
    plan_type: 'aca_marketplace',
    member_id: r.policy_number,
    monthly_premium: null,
    monthly_commission: monthlyCommission,
    effective_date:  r.orig_eff_dt,
    renewal_date:    null,
    termination_date: r.cancel_dt || null,
    status: r.cancel_dt ? 'cancelled' : 'active',
    last_commission_at: r.pd_from_dt || null,
    notes: r.source_vendor ? `Source: ${r.source_vendor}` : null,
    agent_prod_num: producerProdNum || null,
  };
}

// ─────────────────────────────────────────────────────────────────────
// SUMMARY-PAGE PARSER (Page 2 of every HCSC statement).
//
// The summary holds what the detail rows hide: bucket-level YTD with
// chargebacks. The June 30 2025 sample had Group YTD = -$12 with $0
// this period — a clawback from earlier in the year that the
// detail-only importer would miss completely.
//
// Output shape per bucket:
//   { id, label, group, period_hcsc, period_hisc, period_total, ytd,
//     isChargeback (boolean), pulse (number, ytd - period_total) }
//
// The pulse field surfaces "$0 this period but -$12 YTD" without
// the caller having to do the arithmetic — when pulse < 0, the
// chargeback didn't originate this period (it was inherited).
// ─────────────────────────────────────────────────────────────────────

// Map a bucket label as seen on the statement to our enum id.
const BUCKET_BY_PATTERN = [
  { pattern: /commission\s+group\s+sub\s*total/i,                 id: 'group_subtotal'        },
  { pattern: /individual\s+health\s+new\s+business/i,             id: 'individual_health_new' },
  { pattern: /individual\s+dental\s+new\s+business/i,             id: 'individual_dental_new' },
  { pattern: /individual\s+health\s+retention/i,                  id: 'individual_health_ret' },
  { pattern: /individual\s+dental\s+retention/i,                  id: 'individual_dental_ret' },
  { pattern: /commission\s+individual\s+sub\s*total/i,            id: 'individual_subtotal'   },
  { pattern: /^group\s+total$/im,                                 id: 'group_total'           },
  { pattern: /^individual\s+total$/im,                            id: 'individual_total'      },
  { pattern: /prior\s+balance\s+forward/i,                        id: 'prior_balance_forward' },
  { pattern: /grand\s+totals?/i,                                  id: 'grand_total'           },
];

function bucketIdForLabel(label) {
  for (const { pattern, id } of BUCKET_BY_PATTERN) {
    if (pattern.test(label)) return id;
  }
  return null;
}

// Pull "For Period Ending: June 30, 2025" → ISO yyyy-mm-dd.
export function parseHcscPeriodEnding(text) {
  const m = String(text || '').match(/For Period Ending:\s*([A-Za-z]+)\s+(\d{1,2}),?\s+(\d{4})/i);
  if (!m) return null;
  const MONTHS = { january:0,february:1,march:2,april:3,may:4,june:5,july:6,august:7,september:8,october:9,november:10,december:11 };
  const monthIdx = MONTHS[m[1].toLowerCase()];
  if (monthIdx == null) return null;
  const d = new Date(Date.UTC(Number(m[3]), monthIdx, Number(m[2])));
  return d.toISOString().slice(0, 10);
}

// Pull statement sequence number — "107967601 S0001" → "S0001".
export function parseHcscStatementRef(text) {
  const m = String(text || '').match(/\b(S\d{4,})\b/);
  return m ? m[1] : null;
}

// Pull producer number from the header — "Producer Number: 107967601 PR" → "107967601 PR".
export function parseHcscProducer(text) {
  const m = String(text || '').match(/Producer\s+Number:\s*([\d]{6,}\s*[A-Z]{1,3})/i);
  return m ? m[1].trim() : null;
}

// Walk the summary page lines. Each summary row has a label followed
// by 4 money columns: HCSC period, HISC period, statement total, YTD.
// Some labels span multiple lines in PDF copy-paste — we collapse
// whitespace and look for the 4-money tail.
export function parseHcscSummary(text) {
  if (!text) return { buckets: [], statementRef: null, periodEnding: null, producer: null };

  const statementRef = parseHcscStatementRef(text);
  const periodEnding = parseHcscPeriodEnding(text);
  const producer     = parseHcscProducer(text);

  const lines = String(text).split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const buckets = [];

  // Match: "<label>  $h  $i  $total  $ytd" — money is "$N", "-$N", "($N)", or just "0.00".
  const moneyPat = /(-?\$?\(?-?[\d,]+\.\d{2}\)?)/g;
  for (const line of lines) {
    const moneys = line.match(moneyPat) || [];
    if (moneys.length < 4) continue;
    // The 4 money values must be the LAST 4 tokens of the line.
    const lastFour = moneys.slice(-4).map(parseHcscMoney);
    const [hcsc, hisc, total, ytd] = lastFour;
    // Label = everything before the first matched money cell.
    const firstMoneyIdx = line.indexOf(moneys[moneys.length - 4]);
    const label = line.slice(0, firstMoneyIdx).trim();
    if (!label) continue;
    const id = bucketIdForLabel(label);
    if (!id) continue;
    // Pulse = ytd - period_total. Negative pulse with zero/positive
    // period_total = chargeback inherited from a prior statement.
    const pulse = ytd - total;
    const isChargeback = ytd < 0 || pulse < 0;
    buckets.push({
      id,
      label,
      period_hcsc: hcsc,
      period_hisc: hisc,
      period_total: total,
      ytd,
      pulse,
      isChargeback,
    });
  }

  return { buckets, statementRef, periodEnding, producer };
}

// Build health_chargebacks insert rows from a parsed summary.
// Skips the catch-all GRAND/SUBTOTAL rows so we don't double-count;
// only the source buckets (group_subtotal + the individual line items)
// produce chargeback entries.
const CHARGEBACK_SOURCE_IDS = new Set([
  'group_subtotal',
  'individual_health_new',
  'individual_dental_new',
  'individual_health_ret',
  'individual_dental_ret',
]);

export function summaryToChargebackUpserts(summary, { ownerId, carrier = 'BCBS (HCSC)' } = {}) {
  if (!summary?.buckets) return [];
  const ref = summary.statementRef
    ? `HCSC ${summary.statementRef}${summary.periodEnding ? ` · ${summary.periodEnding}` : ''}`
    : (summary.periodEnding ? `HCSC · ${summary.periodEnding}` : 'HCSC statement');
  return summary.buckets
    .filter(b => b.isChargeback && CHARGEBACK_SOURCE_IDS.has(b.id))
    .map(b => ({
      owner_id: ownerId,
      statement_ref: ref,
      bucket: b.id,
      carrier,
      amount: Math.min(b.ytd, 0),         // amount is the negative number itself
      period_amount: b.period_total,
      ytd_amount: b.ytd,
      period_ending: summary.periodEnding || null,
      reason: 'retro_term',                 // best guess for HCSC; agent can edit
      notes: `Auto-detected from ${b.label} — ${b.period_total >= 0 ? 'inherited' : 'this-period'} chargeback. Verify against prior statement.`,
    }));
}

// Provider links helper is re-exported from coverageTemplates so the
// importer UI can offer a quick "open BCBS network finder" link in
// the same module surface.
export { providerLinks };
